import React, { useState, useCallback, useEffect } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { favoritesAPI } from "@/api/favorites";
import IcomoonIcon from "@icons/IcomoonIcon";
import SntTrackerIcon from "@icons/SntTrackerIcon";
import SntGeozoneIcon from "@icons/SntGeozoneIcon";
import SntOrgIcon from "@icons/SntOrgIcon";
import AwesomeIcon from "@icons/AwesomeIcon";
import FavoriteButton, { FavoriteEntityType } from "@cleanup/components/FavoriteButton";
import SntImageView from "@/cleanup/widgets/images/SntImageView";
import RightSideBar from "@cleanup/widgets/RightSideBar/RightSideBar";
import RightSidebarGroup from "@cleanup/widgets/RightSideBar/RightSidebarGroup";
import FavoritesSidebarItem from "./FavoritesSidebarItem";
import GlobalUtils from "@/utils/GlobalUtils";
import GeozoneListImage from "@/pages/Geozone/Image/GeozoneListImage";
import {
  DeviceHomeRoute,
  InfraRoute,
} from "@/cleanup/utils/routesUtil/routesUtil";
import {
  Device,
  Geozone,
  Organization,
  FavoriteItems,
} from "@/types/favorites";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";
import { RootState } from "@/types/RootState";
import * as actions from "@/redux/actions";
import { useOrg } from "@/contexts/OrgContext";

const FavoritesSidebar: React.FC = () => {
  const dispatch = useDispatch();
  const loginInfo = useSelector((state: RootState) => state.user);
  const showFavoritesSidebar = useSelector(
    (state: RootState) => state.all.showFavoritesSidebar,
    shallowEqual
  );
  const history = useHistory();
  const { withOrg } = useOrg();

  const [favoriteItems, setFavoriteItems] = useState<FavoriteItems>({
    devices: [],
    geozones: [],
    organizations: [],
  });
  const [loading, setLoading] = useState<boolean>(false);

  // Check if user can see organizations (only SYSTEM and PARTNER users)
  const canViewOrganizations =
    loginInfo?.organisation?.organisationType === "SYSTEM" ||
    loginInfo?.organisation?.organisationType === "PARTNER";

  const loadFavoriteItems = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await favoritesAPI.getAllFavoriteEntities();

      // Parse the JSON response since backend returns a JSON string
      const data =
        typeof response.data === "string"
          ? JSON.parse(response.data)
          : response.data;
      const { devices = [], organizations = [], geozones = [] } = data;

      // Backend now returns items sorted alphabetically by name
      setFavoriteItems({
        devices: devices,
        geozones: geozones,
        organizations: canViewOrganizations ? organizations : [],
      });
    } catch (error) {
      console.error("Error loading favorite items:", error);
      setFavoriteItems({
        devices: [],
        geozones: [],
        organizations: [],
      });
    } finally {
      setLoading(false);
    }
  }, [canViewOrganizations]);

  // Load favorites when sidebar opens
  useEffect(() => {
    if (showFavoritesSidebar) {
      loadFavoriteItems();
    }
  }, [showFavoritesSidebar, loadFavoriteItems]);

  const handleClose = () => {
    dispatch(actions.setShowFavoritesSidebar(false));
  };

  const handleDeviceClick = (device: Device): void => {
    let url: string;
    if (device.deviceCategory === "INFRA") {
      url = InfraRoute.getUpdatePage({
        type: "view",
        id: device.id,
      });
    } else {
      url = DeviceHomeRoute.getUpdatePage({
        id: device.id,
        tabActive: "asset_details_tab",
      });
    }
    handleClose();
    history.push(withOrg(url));
  };

  const handleGeozoneClick = (geozone: Geozone): void => {
    handleClose();
    history.push(withOrg(`/geozone_individual?id=${geozone.id}`));
  };

  const handleOrganizationClick = (organization: Organization): void => {
    handleClose();
    history.push(`/organization_update?type=view&org=${organization.id}`);
  };

  // Handle favorite toggle callbacks
  const handleDeviceFavoriteToggle = (
    isFavorite: boolean,
    entityType: FavoriteEntityType,
    entityId: string | number
  ): void => {
    if (!isFavorite) {
      setFavoriteItems((prev) => ({
        ...prev,
        devices: prev.devices.filter(
          (device) => device.id.toString() !== entityId
        ),
      }));
    }
  };

  const handleGeozoneFavoriteToggle = (
    isFavorite: boolean,
    entityType: FavoriteEntityType,
    entityId: string | number
  ): void => {
    if (!isFavorite) {
      setFavoriteItems((prev) => ({
        ...prev,
        geozones: prev.geozones.filter(
          (geozone) => geozone.id.toString() !== entityId
        ),
      }));
    }
  };

  const handleOrganizationFavoriteToggle = (
    isFavorite: boolean,
    entityType: FavoriteEntityType,
    entityId: string | number
  ): void => {
    if (!isFavorite) {
      setFavoriteItems((prev) => ({
        ...prev,
        organizations: prev.organizations.filter(
          (org) => org.id.toString() !== entityId
        ),
      }));
    }
  };

  if (!showFavoritesSidebar) {
    return null;
  }

  const hasAnyFavorites =
    favoriteItems.devices.length > 0 ||
    favoriteItems.geozones.length > 0 ||
    favoriteItems.organizations.length > 0;

  return (
    <RightSideBar
      title={translate(LanguageKey.favorites_key)}
      onClose={handleClose}
      loading={loading}
    >
      {!hasAnyFavorites ? (
        <div className="p-4 text-center">
          <div className="text-muted">
            <IcomoonIcon
              icon="star-empty"
              size="24"
              className="mb-2 d-block"
            />
            <small>{translate(LanguageKey.no_favorites_found_key)}</small>
          </div>
        </div>
      ) : (
        <>
          {/* Devices Section */}
          {favoriteItems.devices.length > 0 && (
            <RightSidebarGroup
              icon={<SntTrackerIcon size={24} />}
              title={translate(LanguageKey.assets_key)}
              count={favoriteItems.devices.length}
            >
              {favoriteItems.devices.map((device) => (
                <FavoritesSidebarItem
                  key={`device-${device.id}`}
                  image={
                    <SntImageView
                      src={GlobalUtils.getFile(
                        "device",
                        device.id,
                        "image",
                        device.trackerIcon,
                        "small"
                      )}
                      alt={device.name}
                      width="30"
                      height="30"
                      size="30"
                    />
                  }
                  title={device.name}
                  subtitle={device.serial || device.name}
                  onClick={() => handleDeviceClick(device)}
                  decoratorColor={device.decoratorColor}
                  action={
                    <FavoriteButton
                      entityType="device"
                      entityId={device.id}
                      onToggle={handleDeviceFavoriteToggle}
                      size="sm"
                      tooltip={false}
                    />
                  }
                />
              ))}
            </RightSidebarGroup>
          )}

          {/* Geozones Section */}
          {favoriteItems.geozones.length > 0 && (
            <RightSidebarGroup
              icon={<SntGeozoneIcon size={24} />}
              title={translate(LanguageKey.geozones_key)}
              count={favoriteItems.geozones.length}
            >
              {favoriteItems.geozones.map((geozone) => (
                <FavoritesSidebarItem
                  key={`geozone-${geozone.id}`}
                  image={
                    <GeozoneListImage
                      className=""
                      image={geozone.image}
                      icon={geozone.icon}
                      width="30"
                      height="30"
                    />
                  }
                  title={geozone.name}
                  subtitle={geozone.address || geozone.name}
                  onClick={() => handleGeozoneClick(geozone)}
                  action={
                    <FavoriteButton
                      entityType="geozone"
                      entityId={geozone.id}
                      onToggle={handleGeozoneFavoriteToggle}
                      size="sm"
                      tooltip={false}
                    />
                  }
                />
              ))}
            </RightSidebarGroup>
          )}

          {/* Organizations Section */}
          {favoriteItems.organizations.length > 0 && (
            <RightSidebarGroup
              icon={<SntOrgIcon size={24} />}
              title={translate(LanguageKey.organizations_key)}
              count={favoriteItems.organizations.length}
            >
              {favoriteItems.organizations.map((organization) => (
                <FavoritesSidebarItem
                  key={`org-${organization.id}`}
                  image={<AwesomeIcon icon="user-group" />}
                  title={organization.name}
                  subtitle={organization.status || organization.name}
                  onClick={() => handleOrganizationClick(organization)}
                  action={
                    <FavoriteButton
                      entityType="organization"
                      entityId={organization.id}
                      onToggle={handleOrganizationFavoriteToggle}
                      size="sm"
                      tooltip={false}
                    />
                  }
                />
              ))}
            </RightSidebarGroup>
          )}
        </>
      )}
    </RightSideBar>
  );
};

export default FavoritesSidebar;
